import type { KeyboardEventHandler } from "react";

import { z } from "zod";

export const TAX_ID_TYPES = ["USA_SSN", "USA_ITIN"] as const;
export type TaxIdType = (typeof TAX_ID_TYPES)[number];

export const TAX_ID_TYPE_LABELS: Record<TaxIdType, string> = {
  USA_SSN: "Social Security Number",
  USA_ITIN: "Individual Taxpayer ID",
};

// SSN: area (001-899 excl. 666) + group (01-99) + serial (0001-9999)
const SSN_REGEX = /^(?!000|666|9\d\d)\d{3}-?\d{2}-?\d{4}$/;
// ITIN: starts with 9, group number restricted to IRS-defined ranges
const ITIN_REGEX =
  /^9\d{2}-?((5[0-9]|6[0-5])|(8[3-8])|(9[0-2])|(9[4-9]))-?\d{4}$/;

export const TAX_ID_REGEX: Record<TaxIdType, RegExp> = {
  USA_SSN: SSN_REGEX,
  USA_ITIN: ITIN_REGEX,
};

export const TAX_ID_ERROR_MESSAGES: Record<TaxIdType, string> = {
  USA_SSN: "Invalid SSN format (XXX-XX-XXXX)",
  USA_ITIN: "Invalid ITIN format (9XX-XX-XXXX, valid IRS group required)",
};

export const formatTaxId = (taxId: string): string => {
  const digits = taxId.replace(/\D/g, "");
  if (digits.length === 9) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  }
  return taxId;
};

export const buildTaxIdSchema = <
  T extends readonly [TaxIdType, ...TaxIdType[]],
>(
  allowedTypes: T,
) =>
  z.object({
    tax_id_type: z.enum(allowedTypes, {
      error: "Complete required field.",
    }),
    tax_id: z
      .string({ error: "Complete required field." })
      .min(1, "Complete required field.")
      .transform(formatTaxId),
  });

export const refineTaxId = (
  data: { tax_id_type: TaxIdType; tax_id: string },
  ctx: z.RefinementCtx,
) => {
  const regex = TAX_ID_REGEX[data.tax_id_type];
  if (!regex.test(data.tax_id)) {
    ctx.addIssue({
      path: ["tax_id"],
      code: z.ZodIssueCode.custom,
      message: TAX_ID_ERROR_MESSAGES[data.tax_id_type],
    });
  }
};

export const onTaxIdKeyDown: KeyboardEventHandler<HTMLInputElement> = (
  event,
) => {
  const value = event.currentTarget.value;
  if (value.length === 3 || value.length === 6) {
    if (event.key !== "-" && /[0-9]/.test(String.fromCharCode(event.which))) {
      event.currentTarget.value += "-";
    }
  }
};
